<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, onUnmounted } from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import BaseButton from '@/components/Buttons/BaseButton.vue';
import CardComponent from '@/components/cards/CardComponent.vue';
import ModalDefault from '@/components/Modals/ModalDefault.vue';
import TextInputDefault from '@/components/forms/TextInputDefault.vue';
import {
  EditIcon,
  LockIcon,
  CheckIcon
} from '@/icons';
import AdminLayout from '@/components/layout/AdminLayout.vue';
import { useAuthStore } from '@/stores/auth';
import api from '@/services/api';
import { notificationService } from '@/services/notificationService';
import LoaderDefault from '@/components/common/LoaderDefault.vue';
import { getAxiosErrorMessage } from '@/utils/errorHandler';
import type { AxiosError } from 'axios';
import { UserApiService } from '@/services/userApiService';
import profileImageService from '@/services/profileImageService';

// Auth store
const authStore = useAuthStore();

// ==================== DATA DEFINITIONS FIRST ====================

// User data state - DEFINE THIS FIRST
const userData = reactive({
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  address_street: '',
  address_locality: '',
  address_pinCode: '',
  city: '',
  state: '',
  address_country: '',
  profileImage: '',
  userType: '',
  createdAt: '',
  lastLogin: ''
});

// Profile blob URL
const profileBlobUrl = ref('');

// Loading states
const loading = ref(false);
const initialLoad = ref(true);
const uploadingImage = ref(false);
const passwordLoading = ref(false);

// API error state
const apiError = ref<ApiError | null>(null);

// Password change form state
const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

const passwordErrors = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const passwordSuccess = ref(false);

// Personal profile edit state
const personalEditState = reactive({
  profileImage: '',
  profileImageFile: null as File | null,
  personalInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  },
  addressInfo: {
    street: '',
    locality: '',
    city: '',
    pinCode: '',
    state: '',
    country: 'India'
  }
});

// Image preview
const personalImagePreview = ref('');

// Cropper state
const showCropper = ref(false);
const cropperImage = ref<string>('');

// Modal states
const isPersonalProfileModalOpen = ref(false);

// Form validation errors
const validationErrors = ref<Record<string, string>>({});

// Modal refs
// const fileInputRef = ref<HTMLInputElement>();

// ==================== INTERFACES ====================

interface ApiError {
  message?: string;
  response?: {
    data?: {
      field?: string;
      message?: string;
      [key: string]: unknown;
    };
  };
  [key: string]: unknown;
}

interface AddressInfo {
  street: string;
  locality: string;
  city: string;
  pinCode: string;
  state: string;
  country: string;
}

interface CropData {
  coordinates: {
    width: number;
    height: number;
    left: number;
    top: number;
  };
  canvas: HTMLCanvasElement;
}

// ==================== FUNCTIONS ====================

// Load profile image - NOW userData is defined
const loadProfileImage = async () => {
  if (userData.profileImage) {
    profileBlobUrl.value = await profileImageService.getImageBlobUrl(userData.profileImage);
  } else {
    profileBlobUrl.value = '';
  }
};

// Watch for changes in userData.profileImage
watch(() => userData.profileImage, loadProfileImage);

// Clean up on component unmount
onUnmounted(() => {
  if (profileBlobUrl.value) {
    URL.revokeObjectURL(profileBlobUrl.value);
  }
  profileImageService.clearCache();
});

// Helper function to convert AxiosError to ApiError
const axiosErrorToApiError = (axiosError: AxiosError): ApiError => {
  return {
    message: axiosError.message,
    response: axiosError.response ? {
      data: axiosError.response.data as {
        field?: string;
        message?: string;
        [key: string]: unknown;
      }
    } : undefined,
    code: axiosError.code,
    config: axiosError.config,
    request: axiosError.request,
    isAxiosError: axiosError.isAxiosError,
    toJSON: axiosError.toJSON
  };
};

// Computed properties for display
const fullName = computed(() => {
  return `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Not set';
});

const formattedDate = computed(() => {
  if (!userData.createdAt) return 'Not set';
  return new Date(userData.createdAt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
});

const formattedLastLogin = computed(() => {
  if (!userData.lastLogin) return 'Never';
  return new Date(userData.lastLogin).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
});

// Fetch user profile
const fetchUserProfile = async () => {
  try {
    loading.value = true;
    apiError.value = null;

    const userId = authStore.user?.id;
    if (!userId) {
      throw new Error('No user ID found');
    }

    const response = await api.get(`/api/users/${userId}`);

    if (response.data.success && response.data.data) {
      const user = response.data.data;

      userData.id = user.id || '';
      userData.firstName = user.firstName || user.first_name || '';
      userData.lastName = user.lastName || user.last_name || '';
      userData.email = user.email || '';
      userData.phone = user.phone || '';
      userData.address = user.address || '';
      userData.address_street = user.address_street || '';
      userData.address_locality = user.address_locality || '';
      userData.address_pinCode = user.address_pinCode || '';
      userData.city = user.city || '';
      userData.state = user.state || '';
      userData.address_country = user.address_country || 'India';
      userData.profileImage = user.profileImage || user.profile_image || '';
      userData.userType = user.userType || user.user_type || '';
      userData.createdAt = user.createdAt || user.created_at || '';
      userData.lastLogin = user.lastLogin || user.last_login || '';

      console.log('✅ Profile loaded successfully');

      // Load profile image after user data is set
      await loadProfileImage();
    }
  } catch (error) {
    console.error('❌ Error fetching profile:', error);
    const axiosError = error as AxiosError;
    apiError.value = axiosErrorToApiError(axiosError);
    notificationService.error('Failed to load profile');
  } finally {
    loading.value = false;
    initialLoad.value = false;
  }
};

// Open personal profile modal
const openPersonalProfileModal = () => {
  personalEditState.profileImage = userData.profileImage;
  personalEditState.profileImageFile = null;
  personalEditState.personalInfo = {
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email,
    phone: userData.phone
  };
  personalEditState.addressInfo = {
    street: userData.address_street || '',
    locality: userData.address_locality || '',
    city: userData.city || '',
    pinCode: userData.address_pinCode || '',
    state: userData.state || '',
    country: userData.address_country || 'India'
  };

  personalImagePreview.value = '';
  validationErrors.value = {};
  apiError.value = null;
  showCropper.value = false;
  isPersonalProfileModalOpen.value = true;
};

// Handle image upload
// const handleImageUpload = (event: Event) => {
//   const input = event.target as HTMLInputElement;
//   const file = input.files?.[0];

//   if (file) {
//     const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
//     if (!validTypes.includes(file.type)) {
//       notificationService.error('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
//       return;
//     }

//     if (file.size > 5 * 1024 * 1024) { // Increased to 5MB for better quality
//       notificationService.error('Image size should be less than 5MB');
//       return;
//     }

//     // Read file and show cropper
//     const reader = new FileReader();
//     reader.onload = (e) => {
//       cropperImage.value = e.target?.result as string;
//       showCropper.value = true;
//     };
//     reader.readAsDataURL(file);

//     // Clear the input so the same file can be selected again
//     input.value = '';
//   }
// };

// Handle crop complete
const onCropComplete = ({ canvas }: CropData) => {
  // Convert canvas to blob for preview
  canvas.toBlob((blob: Blob | null) => {
    if (blob) {
      // Clean up old preview URL if exists
      if (personalImagePreview.value) {
        URL.revokeObjectURL(personalImagePreview.value);
      }

      const url = URL.createObjectURL(blob);
      personalImagePreview.value = url;

      // Convert blob to file
      const croppedFile = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
      personalEditState.profileImageFile = croppedFile;
    }
  }, 'image/jpeg', 0.9); // 90% quality for better compression
};

// Cancel cropping
const cancelCrop = () => {
  showCropper.value = false;
  cropperImage.value = '';

  // Clean up preview URL
  if (personalImagePreview.value) {
    URL.revokeObjectURL(personalImagePreview.value);
    personalImagePreview.value = '';
  }

  personalEditState.profileImageFile = null;
};

// Trigger file input
// const triggerFileInput = () => {
//   fileInputRef.value?.click();
// };

// Validate PIN code
const validatePinCode = (pinCode: string): boolean => {
  return /^[0-9]{6}$/.test(pinCode);
};

// Validate address form
const validateForm = (): boolean => {
  const errors: Record<string, string> = {};

  if (!personalEditState.addressInfo.street.trim()) {
    errors.street = 'Address is required';
  }

  if (!personalEditState.addressInfo.locality.trim()) {
    errors.locality = 'Locality/Area is required';
  }

  if (!personalEditState.addressInfo.city.trim()) {
    errors.city = 'City/Town is required';
  }

  if (!personalEditState.addressInfo.state.trim()) {
    errors.state = 'State is required';
  }

  if (!personalEditState.addressInfo.country.trim()) {
    errors.country = 'Country is required';
  }

  if (!personalEditState.addressInfo.pinCode.trim()) {
    errors.pinCode = 'PIN code is required';
  } else if (!validatePinCode(personalEditState.addressInfo.pinCode)) {
    errors.pinCode = 'PIN code must be 6 digits';
  }

  validationErrors.value = errors;
  return Object.keys(errors).length === 0;
};

// Upload profile image using the service
const uploadProfileImage = async (file: File): Promise<string> => {
  const imageUrl = await profileImageService.uploadProfileImage(file);

  // Clear cache for old image
  if (userData.profileImage) {
    profileImageService.clearCache(userData.profileImage);
  }

  return imageUrl;
};

// Save personal profile edits
const savePersonalProfileEdits = async (data: {
  addressInfo: AddressInfo;
  profileImageFile?: File;
}) => {
  if (!validateForm()) {
    notificationService.error('Please fix the validation errors');
    return;
  }

  try {
    loading.value = true;
    apiError.value = null;

    const userId = authStore.user?.id;
    if (!userId) {
      throw new Error('No user ID found');
    }

    let profileImageUrl = userData.profileImage;
    if (data.profileImageFile) {
      uploadingImage.value = true;
      profileImageUrl = await uploadProfileImage(data.profileImageFile);
      uploadingImage.value = false;
    }

    const addressParts = [
      data.addressInfo.street,
      data.addressInfo.locality
    ].filter(Boolean);
    const address = addressParts.join(', ');

    const updateData = {
      address: address,
      address_street: data.addressInfo.street,
      address_locality: data.addressInfo.locality,
      address_pinCode: data.addressInfo.pinCode,
      address_country: data.addressInfo.country,
      city: data.addressInfo.city,
      state: data.addressInfo.state,
      profileImage: profileImageUrl
    };

    await api.put(`/api/users/${userId}`, updateData);

    userData.address = address;
    userData.address_street = data.addressInfo.street;
    userData.address_locality = data.addressInfo.locality;
    userData.address_pinCode = data.addressInfo.pinCode;
    userData.address_country = data.addressInfo.country;
    userData.city = data.addressInfo.city;
    userData.state = data.addressInfo.state;
    userData.profileImage = profileImageUrl;

    authStore.updateProfileImage(profileImageUrl);
    notificationService.success('Profile updated successfully!');
    isPersonalProfileModalOpen.value = false;

    // Reload profile image with new URL
    await loadProfileImage();

  } catch (error: unknown) {
    console.error('Error updating profile:', error);
    const axiosError = error as AxiosError;
    apiError.value = axiosErrorToApiError(axiosError);
    const errorMessage = getAxiosErrorMessage(error);
    notificationService.error(errorMessage);
  } finally {
    loading.value = false;
  }
};

// Password validation
const validatePassword = (password: string): { isValid: boolean; message: string } => {
  if (password.length < 6) {
    return { isValid: false, message: 'Password must be at least 6 characters long' };
  }

  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }

  if (!/[a-z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one lowercase letter' };
  }

  if (!/\d/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one number' };
  }

  return { isValid: true, message: '' };
};

// Validate password form
const validatePasswordForm = (): boolean => {
  let isValid = true;

  passwordErrors.currentPassword = '';
  passwordErrors.newPassword = '';
  passwordErrors.confirmPassword = '';

  if (!passwordForm.currentPassword) {
    passwordErrors.currentPassword = 'Current password is required';
    isValid = false;
  }

  if (!passwordForm.newPassword) {
    passwordErrors.newPassword = 'New password is required';
    isValid = false;
  } else {
    const passwordValidation = validatePassword(passwordForm.newPassword);
    if (!passwordValidation.isValid) {
      passwordErrors.newPassword = passwordValidation.message;
      isValid = false;
    }
  }

  if (!passwordForm.confirmPassword) {
    passwordErrors.confirmPassword = 'Please confirm your new password';
    isValid = false;
  } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    passwordErrors.confirmPassword = 'Passwords do not match';
    isValid = false;
  }

  return isValid;
};

// Clear password error
const clearPasswordError = (field: keyof typeof passwordErrors) => {
  passwordErrors[field] = '';
  passwordSuccess.value = false;
};

// Change password
const changePassword = async () => {
  if (!validatePasswordForm()) {
    return;
  }

  passwordLoading.value = true;
  passwordSuccess.value = false;

  try {
    await UserApiService.changePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    });

    // Clear form
    passwordForm.currentPassword = '';
    passwordForm.newPassword = '';
    passwordForm.confirmPassword = '';

    passwordSuccess.value = true;
    notificationService.success('Password changed successfully!');

    // Hide success message after 5 seconds
    setTimeout(() => {
      passwordSuccess.value = false;
    }, 5000);

  } catch (error: unknown) {
    console.error('Error changing password:', error);
    const errorMessage = getAxiosErrorMessage(error);

    // Check if it's a current password error
    if (errorMessage.toLowerCase().includes('current password') ||
        errorMessage.toLowerCase().includes('incorrect password')) {
      passwordErrors.currentPassword = errorMessage;
    } else {
      notificationService.error(errorMessage);
    }
  } finally {
    passwordLoading.value = false;
  }
};

// Initialize
onMounted(() => {
  fetchUserProfile();
});

// Clean up preview URLs when component unmounts
onUnmounted(() => {
  if (personalImagePreview.value) {
    URL.revokeObjectURL(personalImagePreview.value);
  }
});

// ==================== TEMPLATE STARTS HERE ====================
</script>

<template>
  <AdminLayout>
    <div v-if="initialLoad" class="flex justify-center items-center min-h-[400px]">
      <LoaderDefault title="Loading profile..." />
    </div>

    <div v-else>
      <!-- Main Profile Card -->
      <CardComponent
        title="Personal Profile"
        bodyClass="flex flex-col gap-3"
        :showHeaderBorder="true"
        :showHeaderPaddingBottom="true"
      >
        <template #header>
          <BaseButton
            @click="openPersonalProfileModal"
            text="Edit Profile"
            :icon="EditIcon"
            variant="secondary"
            size="xs"
            :disabled="loading"
          />
        </template>
        <template #body>
          <CardComponent>
            <template #body>
              <div class="flex flex-col items-center w-full gap-6 xl:flex-row">
                <div class="relative w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800">
                  <!-- <img
                    v-if="profileBlobUrl"
                    :src="userData.profileImage"
                    :key="profileBlobUrl"
                    alt="User profile"
                    class="object-cover w-full h-full rounded-full"
                  /> -->
                  <div class="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <span class="text-gray-500 dark:text-gray-400 text-lg font-medium">
                      {{ userData.firstName?.charAt(0) || '' }}{{ userData.lastName?.charAt(0) || '' }}
                    </span>
                  </div>
                </div>
                <div class="order-3 xl:order-2">
                  <h4 class="mb-2 text-lg font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
                    {{ fullName }}
                  </h4>
                  <div class="flex flex-col items-center gap-1 text-center xl:flex-row xl:gap-3 xl:text-left">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      <strong>Role: </strong>
                      <span class="capitalize">{{ userData.userType || 'Admin' }}</span>
                    </p>
                    <div class="hidden h-3.5 w-px bg-gray-300 dark:bg-gray-700 xl:block"></div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      <strong>Joined Date: </strong>
                      <span>{{ formattedDate }}</span>
                    </p>
                    <div class="hidden h-3.5 w-px bg-gray-300 dark:bg-gray-700 xl:block"></div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      <strong>Last Login: </strong>
                      <span>{{ formattedLastLogin }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </template>
          </CardComponent>

          <CardComponent
            title="Personal Information"
            :showHeaderBorder="true"
            :showHeaderPaddingBottom="true"
          >
            <template #body>
              <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">First Name</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.firstName || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">Last Name</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.lastName || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">Email address</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.email || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">Phone</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.phone || 'Not set' }}</p>
                </div>
              </div>
            </template>
          </CardComponent>

          <CardComponent
            title="Communication Address"
            :showHeaderBorder="true"
            :showHeaderPaddingBottom="true"
          >
            <template #body>
              <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">Address</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.address_street || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">Locality</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.address_locality || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">PIN Code</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.address_pinCode || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">City</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.city || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">State</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.state || 'Not set' }}</p>
                </div>
                <div>
                  <p class="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">Country</p>
                  <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ userData.address_country || 'India' }}</p>
                </div>
              </div>
            </template>
          </CardComponent>
        </template>
      </CardComponent>

      <!-- Password Change Card -->
      <CardComponent
        title="Change Password"
        :showHeaderBorder="true"
        :showHeaderPaddingBottom="true"
        class="mt-6"
      >
        <template #body>
          <div class="space-y-6">
            <!-- Success Message -->
            <div
              v-if="passwordSuccess"
              class="p-4 bg-green-50 border border-green-200 rounded-lg"
            >
              <div class="flex">
                <div class="flex-shrink-0">
                  <CheckIcon class="w-5 h-5 text-green-400" />
                </div>
                <div class="ml-3">
                  <p class="text-sm font-medium text-green-800">
                    Password changed successfully!
                  </p>
                </div>
              </div>
            </div>

            <!-- Password Form -->
            <form @submit.prevent="changePassword" class="flex flex-col gap-5 items-start justify-start">
              <TextInputDefault
                v-model="passwordForm.currentPassword"
                :type="showCurrentPassword ? 'text' : 'password'"
                labelText="Current Password"
                placeholder="Enter your current password"
                id="currentPassword"
                name="currentPassword"
                :icon="showCurrentPassword ? 'eye-slash' : 'eye'"
                @icon-click="showCurrentPassword = !showCurrentPassword"
                :errorMessage="passwordErrors.currentPassword"
                @update:modelValue="clearPasswordError('currentPassword')"
                asterisk
              >
                <template #icon>
                  <LockIcon />
                </template>
              </TextInputDefault>

              <TextInputDefault
                v-model="passwordForm.newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                labelText="New Password"
                placeholder="Enter your new password"
                id="newPassword"
                name="newPassword"
                :icon="showNewPassword ? 'eye-slash' : 'eye'"
                @icon-click="showNewPassword = !showNewPassword"
                :errorMessage="passwordErrors.newPassword"
                @update:modelValue="clearPasswordError('newPassword')"
                asterisk
              />

              <div class="mt-1 text-xs text-gray-500 space-y-1">
                <p>Password must:</p>
                <ul class="list-disc list-inside ml-2">
                  <li>Be at least 6 characters long</li>
                  <li>Include uppercase and lowercase letters</li>
                  <li>Include at least one number</li>
                </ul>
              </div>

              <TextInputDefault
                v-model="passwordForm.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                labelText="Confirm New Password"
                placeholder="Confirm your new password"
                id="confirmPassword"
                name="confirmPassword"
                :icon="showConfirmPassword ? 'eye-slash' : 'eye'"
                @icon-click="showConfirmPassword = !showConfirmPassword"
                :errorMessage="passwordErrors.confirmPassword"
                @update:modelValue="clearPasswordError('confirmPassword')"
                asterisk
              />

              <div class="flex justify-end">
                <BaseButton
                  type="submit"
                  text="Change Password"
                  variant="primary"
                  size="md"
                  :disabled="passwordLoading"
                  :loading="passwordLoading"
                />
              </div>
            </form>
          </div>
        </template>
      </CardComponent>

      <!-- Edit Profile Modal -->
      <ModalDefault
        v-model="isPersonalProfileModalOpen"
        :title="showCropper ? 'Crop Image' : 'Edit Profile Image & Address'"
        :width="showCropper ? 'max-w-2xl' : 'max-w-lg'"
        @close="showCropper = false"
      >
        <!-- Cropper View -->
        <div v-if="showCropper" class="flex flex-col gap-4">
          <div class="relative">
            <Cropper
              ref="cropperInstance"
              class="cropper-wrapper"
              :src="cropperImage"
              :stencil-props="{
                aspectRatio: 1,
                movable: true,
                scalable: true,
                rotatable: true,
                minWidth: 100,
                minHeight: 100
              }"
              :resize-image="{
                adjustStencil: false
              }"
              image-restriction="stencil"
              @change="onCropComplete"
              :default-size="{
                width: 400,
                height: 400
              }"
            />
          </div>

          <!-- Help Text -->
          <div class="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
            <p>Use the mouse wheel to zoom in or out</p>
            <p>Click and drag to move the image</p>
          </div>
        </div>

        <!-- Edit Form View -->
        <div v-else class="flex flex-col gap-6">
          <!-- Profile Image Section -->
          <!-- <div class="space-y-4">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Profile Image</h3>
            <div class="flex flex-col items-center gap-4">
              <div class="w-32 h-32 overflow-hidden border-2 border-gray-300 dark:border-gray-600 rounded-full">
                <img
                  :src="personalImagePreview || personalEditState.profileImage || '/images/placeholder/avatar.svg'"
                  alt="Profile Image Preview"
                  class="w-full h-full object-cover"
                  @error="profileImageService.handleImageError"
                />
              </div>
              <div class="mt-4 text-center">
                <input
                  ref="fileInputRef"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleImageUpload"
                />
                <span class="flex gap-4">
                  <BaseButton
                    @click="triggerFileInput"
                    :text="personalImagePreview ? 'Change Image' : 'Upload Image'"
                    variant="secondary"
                    size="sm"
                    :disabled="uploadingImage"
                  />
                  <BaseButton
                    v-if="personalImagePreview"
                    @click="cancelCrop"
                    text="Remove"
                    variant="danger"
                    size="sm"
                    class="ml-2"
                  />
                </span>
              </div>
              <div class="mt-2 text-center">
                <p class="text-xs text-gray-500 dark:text-gray-400">Click upload, then crop to a square for best results</p>
                <p class="text-xs text-gray-400 dark:text-gray-500">Supports: JPG, PNG, GIF, WEBP (max 5MB)</p>
              </div>
            </div>
          </div> -->

          <!-- Address Section -->
          <div class="space-y-4">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Communication Address</h3>
            <div class="grid grid-cols-1 gap-4">
              <div class="col-span-2">
                <TextInputDefault
                  id="street"
                  name="street"
                  type="text"
                  labelText="Address"
                  v-model="personalEditState.addressInfo.street"
                  placeholder="Enter your address"
                  :errorMessage="validationErrors.street"
                  asterisk
                />
              </div>
              <div>
                <TextInputDefault
                  id="locality"
                  name="locality"
                  type="text"
                  labelText="Locality/Area"
                  v-model="personalEditState.addressInfo.locality"
                  placeholder="Enter your locality/area"
                  :errorMessage="validationErrors.locality"
                  asterisk
                />
              </div>
              <div>
                <TextInputDefault
                  id="city"
                  name="city"
                  type="text"
                  labelText="City/Town"
                  v-model="personalEditState.addressInfo.city"
                  placeholder="Enter your city/town"
                  :errorMessage="validationErrors.city"
                  asterisk
                />
              </div>
              <div>
                <TextInputDefault
                  id="state"
                  name="state"
                  type="text"
                  labelText="State"
                  v-model="personalEditState.addressInfo.state"
                  placeholder="Enter your state"
                  :errorMessage="validationErrors.state"
                  asterisk
                />
              </div>
              <div>
                <TextInputDefault
                  id="pinCode"
                  name="pinCode"
                  type="text"
                  labelText="PIN Code"
                  v-model="personalEditState.addressInfo.pinCode"
                  placeholder="Enter 6-digit PIN code"
                  maxlength="6"
                  :errorMessage="validationErrors.pinCode"
                  asterisk
                />
              </div>
              <div>
                <TextInputDefault
                  id="country"
                  name="country"
                  type="text"
                  labelText="Country"
                  v-model="personalEditState.addressInfo.country"
                  placeholder="Enter country"
                  :errorMessage="validationErrors.country"
                  asterisk
                />
              </div>
            </div>

            <!-- Display API Error if any -->
            <div
              v-if="apiError"
              class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <p class="text-xs text-red-600 dark:text-red-400">
                <strong>Error:</strong> {{ apiError.response?.data?.message || apiError.message || 'An unexpected error occurred' }}
              </p>
            </div>

            <div class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p class="text-xs text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> All address fields are required. Name, email, and phone number cannot be edited.
              </p>
            </div>
          </div>
        </div>

        <template #footer>
            <BaseButton
              text="Cancel"
              variant="secondary"
              @click="isPersonalProfileModalOpen = false"
              :disabled="loading || uploadingImage"
              v-if="!showCropper"
            />
            <BaseButton
              text="Save Changes"
              variant="primary"
              @click="() => {
                savePersonalProfileEdits({
                  addressInfo: personalEditState.addressInfo,
                  profileImageFile: personalEditState.profileImageFile || undefined
                });
              }"
              :disabled="loading || uploadingImage"
              v-if="!showCropper"
            />


            <BaseButton
              text="Cancel"
              variant="secondary"
              @click="cancelCrop"
              v-if="showCropper"
            />
            <BaseButton
              text="Apply Crop"
              variant="primary"
              @click="showCropper = false"
              v-if="showCropper"
            />
        </template>
      </ModalDefault>
    </div>
  </AdminLayout>
</template>

<style scoped>
.cropper-wrapper {
  height: 400px;
  width: 100%;
  background: #f3f4f6;
  border-radius: 0.5rem;
}

.dark .cropper-wrapper {
  background: #1f2937;
}

:deep(.cropper-view-box),
:deep(.cropper-face) {
  border-radius: 50%;
}

:deep(.cropper-view-box) {
  outline: 2px solid #3b82f6;
  outline-color: rgba(59, 130, 246, 0.75);
}

:deep(.cropper-line) {
  background-color: #3b82f6;
}

:deep(.cropper-point) {
  background-color: #3b82f6;
  width: 8px;
  height: 8px;
}
</style>
